<template>
  <header
    id="shell.header"
    class="shell-header position-sticky top-0 d-flex flex-shrink-0 align-items-center px-3"
  >
    <aside class="d-flex align-items-center justify-content-between h-100 pe-sm-3">
      <div class="h-100" style="cursor: pointer" @click="$router.push('/')"><LogoEkco /></div>
      <IconButton
        id="shell.header.sidebar-open"
        icon-name="material-symbols:side-navigation"
        variant="ghost"
        size="lg"
        @click="
          open = !open;
          peek = false;
        "
        @mouseenter="if (open === false) peek = true;"
      />
    </aside>
    <slot name="sidebar-toggle" />
    <nav ref="nav" class="d-flex align-items-center justify-content-between gap-1 h-100 w-100">
      <div
        class="flex-grow-1 d-flex align-items-center"
        :style="{'--spotlight-start': spotlightStart}"
      >
        <SpotlightSearch @navigate="navigate" />
      </div>
      <div class="d-flex align-items-center gap-1 flex-shrink-0">
        <IconButton
          id="shell.header.notifications"
          icon-name="material-symbols:notifications-outline"
          size="lg"
          :aria-label="translate('Open notifications')"
          :badge="unreadCount > 0 ? unreadCount.toString() : ''"
          @click="settingsTab = 'Notifications'"
        />
        <HelpDropdown />
        <IconButton
          id="shell.header.feedback"
          icon-name="material-symbols:feedback-outline"
          size="lg"
          @click="handleFeedbackOpen()"
        />
        <IconButton
          v-if="performanceStore.entries.length > 0"
          id="shell.header.performance"
          icon-name="material-symbols:speed-outline"
          size="lg"
          @click="settingsTab = 'Performance'"
        />
        <BootstrapButton
          id="settings-menu-btn"
          name="Open Profile"
          variant="ghost"
          class="btn-profile"
          :aria-label="translate('Open Profile')"
          @click="settingsTab = 'Profile'"
        >
          <div class="d-flex flex-nowrap align-items-center gap-1">
            <div v-if="!isMobile && !isTablet" class="d-flex flex-column flex-nowrap text-end me-1">
              <h6>{{ selectedCustomerName }}</h6>
              <p>{{ userStore.user?.email }}</p>
            </div>
            <img
              v-if="userStore.user?.avatar"
              :src="userStore.user.avatar"
              alt=""
              class="avatar flex-shrink-0 rounded-circle object-fit-cover"
              data-testid="shell.header.avatar"
            />
            <Icon
              v-else
              icon="material-symbols:account-circle-outline"
              class="flex-shrink-0 fs-5"
            />
          </div>
        </BootstrapButton>
        <SettingsApp />
      </div>
    </nav>
  </header>
</template>
<script setup lang="ts">
import {useElementBounding} from '@vueuse/core';
import {feedbackOpenKey} from '@/components/shared/custom/FeedbackSlideIn/injectionKeys';
import {settingsKey} from '@/components/settings/injectionKeys';
import {useGuidedTours} from '@/composables/guidedTours/useGuidedTours';
const handleFeedbackOpen = inject(feedbackOpenKey)!;
const settingsTab = inject(settingsKey)!;
const {translate} = useLocalisation();
const {useTourRegistration} = useGuidedTours();
useTourRegistration('user-invitation');
defineProps<{loading?: boolean}>();
const emit = defineEmits<{
  'toggle-dialog': [value: boolean];
  navigate: [meta: SpherePageMeta<SpherePageType>];
}>();
const userStore = useUserStore();
const performanceStore = usePerformanceStore();
const customersStore = useCustomersStore();
const notificationsStore = useNotificationsStore();
const {isMobile, isTablet} = useBsBreakpoints();
const selectedCustomerName = computed(() => customersStore.selectedCustomer?.customer.name);

const unreadCount = computed(() => notificationsStore.notifications['Unread']?.unread_count ?? 0);

onMounted(notificationsStore.startRefreshing);
onBeforeUnmount(notificationsStore.stopRefreshing);

const open = defineModel<boolean>('open', {required: true});
const peek = defineModel<boolean>('peek', {required: true});

const navigate = (meta: SpherePageMeta<SpherePageType>) => emit('navigate', meta);

// Start the search where the page content starts with the sidebar docked, less the room the logo already takes.
// Based on the sidebar width rather than its current offset, so the search stays put when the sidebar is collapsed.
const nav = useTemplateRef<HTMLElement>('nav');
const {left: navLeft} = useElementBounding(nav);
const spotlightStart = computed(
  () => `max(0px, calc(var(--shell-sidebar-width) + 2rem - ${navLeft.value}px))`, // 2rem: the sidebar's & the page's own spacing
);
</script>
<style lang="scss" scoped>
.shell-header {
  height: var(--shell-header-height);
  background: var(--ekco-gradient);
  background-attachment: fixed;
  z-index: 9;
}

:deep(.btn:not(.bg-body, .dropdown-menu .btn, dialog .btn, .modal .btn)) {
  --bs-btn-color: #ffffff;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-active-color: #ffffff;
  --bs-btn-disabled-color: rgb(255 255 255 / 60%);
}

// The light logo has no contrast on blue; use the white one (as in dark mode).
:deep(.logo-ekco.for-light) {
  display: none;
}

:deep(.logo-ekco.for-dark) {
  display: block;
}

.avatar {
  width: 1.5rem; // Same size as the fallback icon (fs-5)
  height: 1.5rem;
}

.btn-profile {
  /* Match icon buttons in header (Bootstrap's default button padding, see .btn-icon) */
  padding: 0.375rem;
  cursor: pointer;

  h6 {
    font-weight: bolder;
    line-height: 1;
  }

  h6,
  p {
    text-wrap: nowrap;
    margin: 0;
    font-size: 0.75rem;
  }
}

:deep(.logo-ekco) {
  height: 100% !important;
  padding: 0.25rem 0;
}
</style>
